/**
 * ISOListView — "In Search Of" Want List
 * 
 * Users maintain a list of cards they're looking for.
 * Cards can have criteria: max price, min condition, foil pref, priority.
 * Persisted to localStorage (MVP), ready for Supabase migration.
 * 
 * When Supabase is wired up, new card_listings will be matched against
 * ISO entries via the fn_match_iso_on_listing() trigger.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    ISOListItem, CardCondition, ISO_PRIORITY_COLORS,
    CONDITION_MULTIPLIERS, formatPrice, generateId
} from './types';
import {
    Search, Plus, Trash2, Edit3, Check, X, ChevronDown, ChevronUp,
    AlertTriangle, Loader2, Eye, EyeOff, Flame, Star, Sparkles,
    Bell, BellOff, Package, Filter, SortAsc, SortDesc, Download, Upload
} from 'lucide-react';

interface ISOListViewProps {
    currency: 'usd' | 'eur';
}

const STORAGE_KEY = 'deckstr_iso_list';
const CONDITION_ORDER: CardCondition[] = ['NM', 'LP', 'MP', 'HP', 'DMG'];
const DEBOUNCE_MS = 300;

// Load ISO list from localStorage
function loadISOList(): ISOListItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

// Save ISO list to localStorage
function saveISOList(items: ISOListItem[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

type SortField = 'name' | 'priority' | 'price' | 'date' | 'matches';
type SortDir = 'asc' | 'desc';
type FilterMode = 'all' | 'active' | 'paused' | 'high';

// A single printing from Scryfall
interface Printing {
    id: string;
    setCode: string;
    setName: string;
    imageUrl: string;    // normal image
    artCropUrl: string;  // art_crop image
    priceUsd: string | null;
    priceEur: string | null;
    releasedAt: string;
    collectorNumber: string;
    rarity: string;
}

export function ISOListView({ currency }: ISOListViewProps) {
    const [items, setItems] = useState<ISOListItem[]>(() => loadISOList());
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [scryfallResults, setScryfallResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [filterMode, setFilterMode] = useState<FilterMode>('all');
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Printings picker state
    const [printings, setPrintings] = useState<Printing[]>([]);
    const [loadingPrintings, setLoadingPrintings] = useState(false);
    const [selectedPrintingId, setSelectedPrintingId] = useState<string | null>(null);

    // New item form state
    const [newCard, setNewCard] = useState({
        cardName: '',
        imageUrl: '',
        scryfallId: '',
        setCode: '',
        setName: '',
        maxPrice: '' as string,
        minCondition: 'LP' as CardCondition,
        foilPreference: 'either' as 'foil' | 'nonfoil' | 'either',
        quantity: 1,
        priority: 'medium' as 'high' | 'medium' | 'low',
        notes: '',
    });

    // Persist on change
    useEffect(() => {
        saveISOList(items);
    }, [items]);

    // Scryfall autocomplete
    const searchScryfall = useCallback(async (q: string) => {
        if (q.length < 2) {
            setScryfallResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const res = await fetch(
                `https://api.scryfall.com/cards/autocomplete?q=${encodeURIComponent(q)}`
            );
            if (res.ok) {
                const data = await res.json();
                // Fetch image for top results
                const names: string[] = data.data?.slice(0, 8) || [];
                setScryfallResults(names.map(name => ({ name, imageUrl: '' })));

                // Load image for first result
                if (names.length > 0) {
                    const cardRes = await fetch(
                        `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(names[0])}`
                    );
                    if (cardRes.ok) {
                        const cardData = await cardRes.json();
                        setScryfallResults(prev =>
                            prev.map((r, i) => i === 0 ? {
                                ...r,
                                imageUrl: cardData.image_uris?.small || cardData.card_faces?.[0]?.image_uris?.small || '',
                                scryfallId: cardData.id,
                                setCode: cardData.set,
                                setName: cardData.set_name,
                                priceUsd: cardData.prices?.usd,
                                priceEur: cardData.prices?.eur,
                            } : r)
                        );
                    }
                }
            }
        } catch { /* ignore */ }
        setIsSearching(false);
    }, []);

    // Debounced search
    const handleSearchInput = (value: string) => {
        setNewCard(prev => ({ ...prev, cardName: value }));
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => searchScryfall(value), DEBOUNCE_MS);
    };

    // Select a card from autocomplete — fetch default printing + all printings
    const selectCard = async (name: string) => {
        setNewCard(prev => ({ ...prev, cardName: name }));
        setScryfallResults([]);
        setPrintings([]);
        setSelectedPrintingId(null);

        // Fetch default card data
        try {
            const res = await fetch(`https://api.scryfall.com/cards/named?exact=${encodeURIComponent(name)}`);
            if (res.ok) {
                const card = await res.json();
                const imgUrl = card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal || '';
                setNewCard(prev => ({
                    ...prev,
                    cardName: card.name,
                    imageUrl: imgUrl,
                    scryfallId: card.id,
                    setCode: card.set,
                    setName: card.set_name,
                }));
                setSelectedPrintingId(card.id);

                // Now fetch all printings
                fetchPrintings(card.name);
            }
        } catch { /* ignore */ }
    };

    // Fetch all printings for a card name
    const fetchPrintings = async (cardName: string) => {
        setLoadingPrintings(true);
        try {
            // Scryfall search with unique prints
            const q = `!"${cardName}" unique:prints`;
            const res = await fetch(
                `https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}&order=released&dir=desc`
            );
            if (res.ok) {
                const data = await res.json();
                const allPrintings: Printing[] = (data.data || []).map((c: any) => ({
                    id: c.id,
                    setCode: c.set,
                    setName: c.set_name,
                    imageUrl: c.image_uris?.normal || c.card_faces?.[0]?.image_uris?.normal || '',
                    artCropUrl: c.image_uris?.art_crop || c.card_faces?.[0]?.image_uris?.art_crop || '',
                    priceUsd: c.prices?.usd || c.prices?.usd_foil || null,
                    priceEur: c.prices?.eur || c.prices?.eur_foil || null,
                    releasedAt: c.released_at || '',
                    collectorNumber: c.collector_number || '',
                    rarity: c.rarity || 'common',
                }));
                setPrintings(allPrintings);
            }
        } catch { /* ignore */ }
        setLoadingPrintings(false);
    };

    // Select a specific printing
    const selectPrinting = (p: Printing) => {
        setSelectedPrintingId(p.id);
        setNewCard(prev => ({
            ...prev,
            imageUrl: p.imageUrl,
            scryfallId: p.id,
            setCode: p.setCode,
            setName: p.setName,
        }));
    };

    // Add new ISO item
    const addItem = () => {
        if (!newCard.cardName.trim()) return;

        const now = new Date().toISOString();
        const item: ISOListItem = {
            id: generateId(),
            cardName: newCard.cardName.trim(),
            scryfallId: newCard.scryfallId || undefined,
            setCode: newCard.setCode || undefined,
            setName: newCard.setName || undefined,
            maxPrice: newCard.maxPrice ? parseFloat(newCard.maxPrice) : null,
            minCondition: newCard.minCondition,
            foilPreference: newCard.foilPreference,
            quantity: newCard.quantity,
            priority: newCard.priority,
            notes: newCard.notes || undefined,
            imageUrl: newCard.imageUrl || undefined,
            isActive: true,
            matchedCount: 0,
            createdAt: now,
            updatedAt: now,
        };

        setItems(prev => [item, ...prev]);
        resetForm();
    };

    // Reset form
    const resetForm = () => {
        setNewCard({
            cardName: '', imageUrl: '', scryfallId: '', setCode: '', setName: '',
            maxPrice: '', minCondition: 'LP', foilPreference: 'either',
            quantity: 1, priority: 'medium', notes: '',
        });
        setScryfallResults([]);
        setPrintings([]);
        setSelectedPrintingId(null);
        setIsAdding(false);
    };

    // Remove item
    const removeItem = (id: string) => {
        setItems(prev => prev.filter(i => i.id !== id));
        if (expandedItem === id) setExpandedItem(null);
    };

    // Toggle active/paused
    const toggleActive = (id: string) => {
        setItems(prev => prev.map(i =>
            i.id === id ? { ...i, isActive: !i.isActive, updatedAt: new Date().toISOString() } : i
        ));
    };

    // Update priority
    const cyclePriority = (id: string) => {
        const order: ('low' | 'medium' | 'high')[] = ['low', 'medium', 'high'];
        setItems(prev => prev.map(i => {
            if (i.id !== id) return i;
            const idx = order.indexOf(i.priority);
            return { ...i, priority: order[(idx + 1) % 3], updatedAt: new Date().toISOString() };
        }));
    };

    // Filtered & sorted items
    const displayItems = useMemo(() => {
        let filtered = items;

        // Filter mode
        switch (filterMode) {
            case 'active': filtered = filtered.filter(i => i.isActive); break;
            case 'paused': filtered = filtered.filter(i => !i.isActive); break;
            case 'high': filtered = filtered.filter(i => i.priority === 'high'); break;
        }

        // Text filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(i =>
                i.cardName.toLowerCase().includes(q) ||
                i.notes?.toLowerCase().includes(q) ||
                i.setName?.toLowerCase().includes(q)
            );
        }

        // Sort
        const priorityRank = { high: 3, medium: 2, low: 1 };
        filtered.sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case 'name': cmp = a.cardName.localeCompare(b.cardName); break;
                case 'priority': cmp = priorityRank[a.priority] - priorityRank[b.priority]; break;
                case 'price': cmp = (a.maxPrice ?? 0) - (b.maxPrice ?? 0); break;
                case 'date': cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(); break;
                case 'matches': cmp = a.matchedCount - b.matchedCount; break;
            }
            return sortDir === 'desc' ? -cmp : cmp;
        });

        return filtered;
    }, [items, filterMode, searchQuery, sortField, sortDir]);

    // Stats
    const totalItems = items.length;
    const activeItems = items.filter(i => i.isActive).length;
    const highPriority = items.filter(i => i.priority === 'high' && i.isActive).length;
    const totalMatches = items.reduce((sum, i) => sum + i.matchedCount, 0);

    // Export as text
    const exportList = () => {
        const text = items.map(i => {
            let line = `${i.quantity}x ${i.cardName}`;
            if (i.setName) line += ` [${i.setCode?.toUpperCase()}]`;
            if (i.maxPrice) line += ` (max $${i.maxPrice})`;
            line += ` — ${i.priority} priority, ${i.minCondition}+`;
            if (i.foilPreference !== 'either') line += `, ${i.foilPreference}`;
            if (i.notes) line += ` // ${i.notes}`;
            return line;
        }).join('\n');

        navigator.clipboard.writeText(text).catch(() => { });
    };

    return (
        <div className="space-y-4">
            {/* ========== HEADER ========== */}
            <div className="bg-gradient-to-r from-rose-900/20 via-amber-900/15 to-rose-900/20 border border-rose-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/30 flex items-center justify-center">
                            <Sparkles className="size-5 text-rose-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">ISO List</h3>
                            <p className="text-xs text-white/40">
                                Track cards you're searching for — get notified when they're listed
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => { setIsAdding(true); setTimeout(() => inputRef.current?.focus(), 100); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 rounded-lg text-xs font-medium transition-all border border-rose-500/30"
                    >
                        <Plus className="size-3.5" />
                        Add Card
                    </button>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-4 text-[11px]">
                    <span className="text-white/40">
                        <span className="text-white/70 font-medium">{totalItems}</span> cards tracked
                    </span>
                    <span className="text-emerald-400/60">
                        <span className="text-emerald-400 font-medium">{activeItems}</span> active
                    </span>
                    {highPriority > 0 && (
                        <span className="text-rose-400/60 flex items-center gap-1">
                            <Flame className="size-3" />
                            <span className="text-rose-400 font-medium">{highPriority}</span> high priority
                        </span>
                    )}
                    {totalMatches > 0 && (
                        <span className="text-amber-400/60 flex items-center gap-1">
                            <Bell className="size-3" />
                            <span className="text-amber-400 font-medium">{totalMatches}</span> matches
                        </span>
                    )}
                </div>
            </div>

            {/* ========== ADD CARD FORM ========== */}
            {isAdding && (
                <div className="bg-white/[0.04] border border-rose-500/20 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold text-white">Add to ISO List</h4>
                        <button onClick={resetForm} className="text-white/30 hover:text-white/60">
                            <X className="size-4" />
                        </button>
                    </div>

                    {/* Card name search */}
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={newCard.cardName}
                            onChange={e => handleSearchInput(e.target.value)}
                            placeholder="Search card name..."
                            className="w-full pl-8 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-rose-500/40"
                        />
                        {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-white/30 animate-spin" />
                        )}

                        {/* Autocomplete dropdown */}
                        {scryfallResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                                {scryfallResults.map((r, i) => (
                                    <button
                                        key={i}
                                        onClick={() => selectCard(r.name)}
                                        className="w-full px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2 transition-colors"
                                    >
                                        {r.imageUrl && (
                                            <img src={r.imageUrl} alt="" className="w-6 h-9 rounded object-cover flex-shrink-0" />
                                        )}
                                        <span className="truncate">{r.name}</span>
                                        {r.priceUsd && (
                                            <span className="ml-auto text-[10px] text-amber-400/60">${r.priceUsd}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Card image preview + criteria */}
                    <div className="flex gap-3">
                        {/* Image preview — proper MTG card ratio (63:88 ≈ 5:7) */}
                        {newCard.imageUrl && (
                            <div className="flex-shrink-0" style={{ width: '120px' }}>
                                <img
                                    src={newCard.imageUrl}
                                    alt={newCard.cardName}
                                    className="w-full rounded-lg border border-white/10"
                                    style={{ aspectRatio: '63/88' }}
                                />
                                {newCard.setName && (
                                    <div className="mt-1 text-center">
                                        <span className="text-[9px] text-white/30">{newCard.setName}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Criteria grid */}
                        <div className="flex-1 grid grid-cols-2 gap-2">
                            {/* Priority */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Priority</label>
                                <select
                                    value={newCard.priority}
                                    onChange={e => setNewCard(prev => ({ ...prev, priority: e.target.value as any }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                >
                                    <option value="high">🔥 High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div>

                            {/* Quantity */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Qty Needed</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={99}
                                    value={newCard.quantity}
                                    onChange={e => setNewCard(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                />
                            </div>

                            {/* Min condition */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Min Condition</label>
                                <select
                                    value={newCard.minCondition}
                                    onChange={e => setNewCard(prev => ({ ...prev, minCondition: e.target.value as CardCondition }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                >
                                    {CONDITION_ORDER.map(c => (
                                        <option key={c} value={c}>{c} — {CONDITION_MULTIPLIERS[c].label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Max price */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Max Price</label>
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-white/30">
                                        {currency === 'eur' ? '€' : '$'}
                                    </span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min={0}
                                        value={newCard.maxPrice}
                                        onChange={e => setNewCard(prev => ({ ...prev, maxPrice: e.target.value }))}
                                        placeholder="Any"
                                        className="w-full pl-5 pr-2 bg-white/5 border border-white/10 rounded-lg py-1.5 text-xs text-white placeholder:text-white/20 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Foil preference */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Foil</label>
                                <select
                                    value={newCard.foilPreference}
                                    onChange={e => setNewCard(prev => ({ ...prev, foilPreference: e.target.value as any }))}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                >
                                    <option value="either">Either</option>
                                    <option value="foil">Foil Only</option>
                                    <option value="nonfoil">Non-Foil Only</option>
                                </select>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="text-[10px] text-white/30 uppercase tracking-wider mb-1 block">Notes</label>
                                <input
                                    type="text"
                                    value={newCard.notes}
                                    onChange={e => setNewCard(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="e.g., need Strixhaven art"
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder:text-white/20 focus:outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ===== PRINTINGS / SET PICKER ===== */}
                    {(printings.length > 0 || loadingPrintings) && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-[10px] text-white/30 uppercase tracking-wider">Choose Printing / Art</label>
                                {printings.length > 0 && (
                                    <span className="text-[10px] text-white/20">{printings.length} printings</span>
                                )}
                            </div>

                            {loadingPrintings ? (
                                <div className="flex items-center justify-center py-4 text-white/30">
                                    <Loader2 className="size-4 animate-spin mr-2" />
                                    <span className="text-xs">Loading printings...</span>
                                </div>
                            ) : (
                                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
                                    {/* "Any" option */}
                                    <button
                                        onClick={() => {
                                            setSelectedPrintingId(null);
                                            setNewCard(prev => ({ ...prev, setCode: '', setName: '', scryfallId: '' }));
                                        }}
                                        className={`flex-shrink-0 flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all ${selectedPrintingId === null
                                                ? 'border-rose-500/40 bg-rose-500/10'
                                                : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                                            }`}
                                        style={{ width: '72px' }}
                                    >
                                        <div className="w-14 rounded bg-white/5 border border-white/10 flex items-center justify-center" style={{ aspectRatio: '63/88' }}>
                                            <Star className="size-4 text-white/20" />
                                        </div>
                                        <span className="text-[8px] text-white/40 leading-tight text-center">Any Set</span>
                                    </button>

                                    {printings.map(p => {
                                        const isSelected = selectedPrintingId === p.id;
                                        const price = currency === 'eur' ? p.priceEur : p.priceUsd;
                                        const rarityColors: Record<string, string> = {
                                            mythic: 'text-orange-400', rare: 'text-amber-400',
                                            uncommon: 'text-gray-300', common: 'text-gray-500',
                                        };
                                        return (
                                            <button
                                                key={p.id}
                                                onClick={() => selectPrinting(p)}
                                                className={`flex-shrink-0 flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all ${isSelected
                                                        ? 'border-rose-500/40 bg-rose-500/10 ring-1 ring-rose-500/20'
                                                        : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
                                                    }`}
                                                style={{ width: '72px' }}
                                                title={`${p.setName} (#${p.collectorNumber})`}
                                            >
                                                {/* Art crop thumbnail */}
                                                <img
                                                    src={p.artCropUrl || p.imageUrl}
                                                    alt={p.setName}
                                                    className="w-14 rounded border border-white/10"
                                                    style={{ aspectRatio: p.artCropUrl ? '3/2' : '63/88' }}
                                                />
                                                {/* Set code + rarity */}
                                                <span className={`text-[8px] font-bold uppercase leading-tight ${rarityColors[p.rarity] || 'text-white/40'}`}>
                                                    {p.setCode}
                                                </span>
                                                {/* Price */}
                                                {price && (
                                                    <span className="text-[8px] text-amber-400/50 tabular-nums">
                                                        {currency === 'eur' ? '€' : '$'}{price}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Add button */}
                    <button
                        onClick={addItem}
                        disabled={!newCard.cardName.trim()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 rounded-lg text-sm font-medium transition-all border border-rose-500/30 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <Plus className="size-4" />
                        Add to ISO List
                    </button>
                </div>
            )}

            {/* ========== FILTER BAR ========== */}
            {items.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Filter pills */}
                    {(['all', 'active', 'paused', 'high'] as FilterMode[]).map(mode => {
                        const labels: Record<FilterMode, string> = {
                            all: 'All', active: 'Active', paused: 'Paused', high: '🔥 High',
                        };
                        const counts: Record<FilterMode, number> = {
                            all: totalItems,
                            active: activeItems,
                            paused: totalItems - activeItems,
                            high: highPriority,
                        };
                        return (
                            <button
                                key={mode}
                                onClick={() => setFilterMode(mode)}
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors border ${filterMode === mode
                                    ? 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                                    : 'bg-white/5 text-white/40 border-white/10 hover:text-white/60'
                                    }`}
                            >
                                {labels[mode]}
                                <span className="ml-1 opacity-50">{counts[mode]}</span>
                            </button>
                        );
                    })}

                    <div className="flex-1" />

                    {/* Sort */}
                    <select
                        value={sortField}
                        onChange={e => setSortField(e.target.value as SortField)}
                        className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white/50 focus:outline-none"
                    >
                        <option value="date">Sort: Date Added</option>
                        <option value="name">Sort: Name</option>
                        <option value="priority">Sort: Priority</option>
                        <option value="price">Sort: Max Price</option>
                        <option value="matches">Sort: Matches</option>
                    </select>

                    <button
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                        className="p-1 bg-white/5 border border-white/10 rounded-lg text-white/30 hover:text-white/60"
                    >
                        {sortDir === 'desc' ? <SortDesc className="size-3.5" /> : <SortAsc className="size-3.5" />}
                    </button>

                    {/* Search */}
                    <div className="relative w-40">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-white/30" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Filter..."
                            className="w-full pl-7 pr-2 py-1 bg-white/5 border border-white/10 rounded-lg text-[10px] text-white placeholder:text-white/25 focus:outline-none"
                        />
                    </div>

                    {/* Export */}
                    <button
                        onClick={exportList}
                        title="Copy list to clipboard"
                        className="p-1 bg-white/5 border border-white/10 rounded-lg text-white/30 hover:text-white/60"
                    >
                        <Download className="size-3.5" />
                    </button>
                </div>
            )}

            {/* ========== ITEM LIST ========== */}
            {displayItems.length > 0 && (
                <div className="space-y-2">
                    {displayItems.map(item => {
                        const isExpanded = expandedItem === item.id;
                        const priorityStyle = ISO_PRIORITY_COLORS[item.priority];

                        return (
                            <div
                                key={item.id}
                                className={`border rounded-xl overflow-hidden transition-all ${item.isActive
                                    ? 'bg-white/[0.03] border-white/10 hover:border-white/20'
                                    : 'bg-white/[0.01] border-white/5 opacity-50'
                                    }`}
                            >
                                {/* Item header */}
                                <div className="flex items-center gap-3 px-3 py-2.5">
                                    {/* Card image — proper MTG ratio */}
                                    {item.imageUrl ? (
                                        <img
                                            src={item.imageUrl}
                                            alt={item.cardName}
                                            className="rounded border border-white/10 flex-shrink-0"
                                            style={{ width: '32px', aspectRatio: '63/88' }}
                                        />
                                    ) : (
                                        <div
                                            className="rounded bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0"
                                            style={{ width: '32px', aspectRatio: '63/88' }}
                                        >
                                            <Package className="size-3.5 text-white/20" />
                                        </div>
                                    )}

                                    {/* Card info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-white truncate">{item.cardName}</span>
                                            {item.quantity > 1 && (
                                                <span className="text-[10px] text-white/40">×{item.quantity}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {/* Priority badge */}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); cyclePriority(item.id); }}
                                                className="text-[9px] px-1.5 py-0.5 rounded-full border cursor-pointer hover:opacity-80 transition-opacity"
                                                style={{
                                                    backgroundColor: priorityStyle.bg,
                                                    borderColor: priorityStyle.border,
                                                    color: priorityStyle.text,
                                                }}
                                                title="Click to cycle priority"
                                            >
                                                {priorityStyle.label}
                                            </button>
                                            {/* Condition */}
                                            <span className="text-[10px] text-white/30">{item.minCondition}+</span>
                                            {/* Foil */}
                                            {item.foilPreference !== 'either' && (
                                                <span className="text-[9px] text-purple-400/60 border border-purple-400/20 px-1 rounded">
                                                    {item.foilPreference === 'foil' ? '✨ Foil' : 'Non-Foil'}
                                                </span>
                                            )}
                                            {/* Set */}
                                            {item.setCode && (
                                                <span className="text-[10px] text-white/20">{item.setCode.toUpperCase()}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Max price */}
                                    {item.maxPrice != null && (
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-xs text-amber-400/80 font-medium tabular-nums">
                                                ≤ {formatPrice(item.maxPrice, currency)}
                                            </div>
                                            <div className="text-[9px] text-white/20">max price</div>
                                        </div>
                                    )}

                                    {/* Match count */}
                                    {item.matchedCount > 0 && (
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded-full flex-shrink-0">
                                            <Bell className="size-3 text-amber-400" />
                                            <span className="text-[10px] text-amber-400 font-medium">{item.matchedCount}</span>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => toggleActive(item.id)}
                                            title={item.isActive ? 'Pause' : 'Resume'}
                                            className="p-1 text-white/20 hover:text-white/50 transition-colors"
                                        >
                                            {item.isActive ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                                        </button>
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            title="Remove"
                                            className="p-1 text-white/20 hover:text-red-400 transition-colors"
                                        >
                                            <Trash2 className="size-3.5" />
                                        </button>
                                        <button
                                            onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                                            className="p-1 text-white/20 hover:text-white/50 transition-colors"
                                        >
                                            {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded details */}
                                {isExpanded && (
                                    <div className="px-3 pb-3 pt-1 border-t border-white/5 space-y-2">
                                        {item.notes && (
                                            <div className="text-xs text-white/40 italic">"{item.notes}"</div>
                                        )}
                                        <div className="grid grid-cols-3 gap-2 text-[10px]">
                                            <div className="bg-white/5 rounded-lg px-2 py-1.5">
                                                <span className="text-white/30">Condition:</span>
                                                <span className="ml-1 text-white/60">{item.minCondition} or better</span>
                                            </div>
                                            <div className="bg-white/5 rounded-lg px-2 py-1.5">
                                                <span className="text-white/30">Foil:</span>
                                                <span className="ml-1 text-white/60">{item.foilPreference}</span>
                                            </div>
                                            <div className="bg-white/5 rounded-lg px-2 py-1.5">
                                                <span className="text-white/30">Added:</span>
                                                <span className="ml-1 text-white/60">
                                                    {new Date(item.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        {item.setName && (
                                            <div className="text-[10px] text-white/25">
                                                Preferred printing: {item.setName} ({item.setCode?.toUpperCase()})
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ========== EMPTY STATES ========== */}
            {items.length === 0 && !isAdding && (
                <div className="flex flex-col items-center justify-center h-52 text-white/30">
                    <Sparkles className="size-10 mb-3 opacity-20" />
                    <h3 className="text-sm font-medium text-white/50 mb-1">Your ISO list is empty</h3>
                    <p className="text-[11px] text-center max-w-sm mb-4">
                        Build a want list of cards you're looking for. When someone lists a matching card,
                        you'll get notified.
                    </p>
                    <button
                        onClick={() => { setIsAdding(true); setTimeout(() => inputRef.current?.focus(), 100); }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-400 rounded-lg text-xs font-medium transition-all border border-rose-500/30"
                    >
                        <Plus className="size-3.5" />
                        Add Your First Card
                    </button>
                </div>
            )}

            {displayItems.length === 0 && items.length > 0 && !isAdding && (
                <div className="flex flex-col items-center justify-center h-32 text-white/30">
                    <Search className="size-8 mb-2 opacity-20" />
                    <p className="text-xs">No items match your current filters</p>
                </div>
            )}

            {/* ========== FOOTER ========== */}
            {items.length > 0 && (
                <div className="flex items-center gap-2 px-1 text-[10px] text-white/20">
                    <Bell className="size-3 flex-shrink-0" />
                    <p>
                        ISO matching is coming soon — when other DECKSTR.GG users list cards you're looking for,
                        you'll receive notifications here. Data is currently saved locally in your browser.
                    </p>
                </div>
            )}
        </div>
    );
}
